/**
 * THE PLOT PLANNER — Licenced Product (expanded, 12 sections)
 * ──────────────────────────────────────────────────────────
 * A pre-writing workbook: plan a novel (standalone / duology / series) before
 * drafting. Native to the app — same Firebase Auth, same server-mediated licence
 * gate, same account-synced saving as the Publishing Roadmap (all shared via
 * src/licensing/). The plan is stored per-licence in `plannerProgress` (server-
 * locked; reachable only via get/savePlannerProgress). Behaviour + styling match
 * the prototype plot-planner-demo-kqaq.html; styling is scoped in PlotPlanner.css
 * under `.plot-planner`.
 *
 * The product ships BLANK — no baked-in demo plan. The kqaq "Kingdom of Queries
 * and Quests" content is the author's own book, seeded separately as her own
 * saved copy; it is never the default state. Per-field examples are on by default.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PlotPlanner.css';
import { Shield, LogOut } from 'lucide-react';
import RoadmapFeedbackWidget from './RoadmapFeedbackWidget';
import ToolNav from './toolkit/ToolNav';
import { getPlannerProgressFn, savePlannerProgressFn, PLANNER_DARK } from './licensing/licenceApi';
import useAntiCopy from './licensing/useAntiCopy';
import useLicence from './licensing/useLicence';
import PricingPlans from './licensing/PricingPlans';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import useCollabPlan from './plotPlannerCollab/useCollabPlan';
import PlanPicker from './plotPlannerCollab/PlanPicker';
import CollaboratorControl from './plotPlannerCollab/CollaboratorControl';
import { createPlanFn, invitePlanCollaboratorFn, removeCollaboratorFn } from './plotPlannerCollab/planCallables';
import { resolveHouseColour, resolveAllegianceName } from './plotPlannerDynasty';

/* ═══════════════════════════════════════════════════════════
   STATIC DATA
═══════════════════════════════════════════════════════════ */
const uid = () => 'x' + Math.random().toString(36).slice(2, 9);

function fill(str, core) {
  return String(str)
    .replace(/\{hero\}/g, (core.hero || '').trim() || 'your protagonist')
    .replace(/\{title\}/g, (core.title || '').trim() || 'your book');
}

// [key, label, prompt]  — quick physical reference, feeds Cast at a Glance.
const APPEARANCE_FIELDS = [
  ['eyes', 'Eyes', 'colour'],
  ['hair', 'Hair', 'colour · length · style'],
  ['build', 'Build', 'body type'],
  ['height', 'Height', 'tall / average / short'],
  ['features', 'Distinguishing features', 'scars · tattoos · marks'],
];
// [key, label, prompt, example] — "role" handled in the card title.
const CAST_FIELDS = [
  ['goal',  'External goal (what they’re chasing)', '', 'get paid and get out — then, quietly, to matter to someone'],
  ['want',  'Private want (what they really need)', '', 'to stop running from a place he wronged'],
  ['wound', 'Wound / fear', '', 'he failed people once and it’s easier not to try'],
  ['lie',   'False belief they carry', '', 'caring about anything is how you lose it'],
  ['arc',   'Arc in a sentence', '', 'from mercenary drifter to someone who stays'],
  ['voice', 'Voice / distinctive detail', '', 'dry, deflecting; counts tides under his breath'],
  ['notes', 'Notes', '', ''],
];
const REL_FIELDS = [
  ['now',   'Current dynamic', 'where the bond stands right now', 'wary allies; she needs his boat, he needs her silence'],
  ['block', 'What’s blocking them', 'the thing keeping them apart', 'she thinks he’ll sell her out; he thinks he doesn’t deserve trust'],
  ['next',  'Next shift the story has earned', 'the next move — not the endgame', 'he refuses good money to protect her; neither mentions it'],
];
const TIERS = [
  ['primary', 'Primary — main characters'],
  ['secondary', 'Secondary — side characters'],
  ['tertiary', 'Tertiary — everyone else'],
];
const TIER_OPTS = [
  ['primary', 'Primary — main'],
  ['secondary', 'Secondary — side'],
  ['tertiary', 'Tertiary — minor'],
];

// Tie types for the Dynasty Map. (House colours are now derived per-house from a
// stable name hash — see src/plotPlannerDynasty.js — not a fixed palette.)
const LINK_TYPES = {
  married:    { label: 'Married' },
  married_m:  { label: 'Married · male line' },
  married_f:  { label: 'Married · female line' },
  married_o:  { label: 'Married · other line' },
  claimed:    { label: 'Betrothed / claimed' },
  siblings:   { label: 'Siblings' },
  parent:     { label: 'Parent → child' },
  allied:     { label: 'Allied' },
  feud:       { label: 'Rivals / feud' },
  bonded:     { label: 'Bonded' },
};
function txtOn(hex) {
  hex = String(hex || '').replace('#', '');
  if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
  if (hex.length < 6) return '#241f17';
  const r = parseInt(hex.substr(0, 2), 16), g = parseInt(hex.substr(2, 2), 16), b = parseInt(hex.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#241f17' : '#fff';
}

// Generic act structures — NOT the kqaq Kingdom content. {hero}/{title} are filled live.
const STRUCTURES = {
  standalone: {
    intro: 'One complete arc, resolved. A classic four-act spine at roughly a quarter each — setup, rising complications, the turn, and the payoff. Everything opened in this book closes in this book.',
    eyebrow: 'Standalone shape · one book, one full arc',
    acts: [
      { roman: 'I', name: 'Setup & Inciting Incident', pct: '0–25%', purpose: 'Establish the ordinary world, the protagonist’s want, and the event that breaks the status quo.',
        beats: ['Open in the ordinary world — show what {hero} has to lose', 'The inciting incident forces a choice', '{hero} commits to the journey by the end of the act'] },
      { roman: 'II', name: 'Rising Action & Complications', pct: '25–50%', purpose: 'Escalating obstacles; the protagonist tries and fails, learns the real shape of the problem.',
        beats: ['First real attempt — it fails and makes things worse', 'Allies, enemies and the world’s rules revealed', 'Midpoint: a truth that reframes the whole problem'] },
      { roman: 'III', name: 'The Turn & Dark Night', pct: '50–75%', purpose: 'The stakes sharpen, a major setback lands, all seems lost.',
        beats: ['The antagonist’s strongest move', 'All-is-lost moment — the lowest point', '{hero} finds the resolve (or the missing piece) to go on'] },
      { roman: 'IV', name: 'Climax & Resolution', pct: '75–100%', purpose: 'The final confrontation and the new normal. Every open thread closes.',
        beats: ['The climax — {hero} faces the central problem head-on', 'The dramatic question is answered', 'Resolution: the changed world, the changed {hero}'] },
    ],
  },
  duology: {
    intro: 'Two books, one larger arc. Book One is a complete story that also cracks open a bigger one; Book Two resolves everything. Each book still needs its own beginning, middle and satisfying end — the cliffhanger is emotional, not a cut-to-black.',
    eyebrow: 'Duology shape · two books, one over-arc',
    acts: [
      { roman: 'I', name: 'Book 1 · Setup', pct: 'Bk1 0–25%', purpose: 'Establish world, protagonist and the immediate problem that drives Book One.',
        beats: ['Ordinary world and what {hero} wants', 'Inciting incident of Book One’s contained story', 'Commit to the Book One journey'] },
      { roman: 'II', name: 'Book 1 · Rising & Midpoint', pct: 'Bk1 25–75%', purpose: 'Book One’s complications escalate; the midpoint hints at the larger threat beneath.',
        beats: ['Try/fail cycles on the Book One problem', 'Midpoint: the bigger picture peeks through', 'First glimpse of the over-arc antagonist or stakes'] },
      { roman: 'III', name: 'Book 1 · Climax + The Crack', pct: 'Bk1 75–100%', purpose: 'Resolve Book One’s contained story — but reveal the larger conflict it was hiding. A COMPLETE ending that opens a door.',
        beats: ['Book One’s own climax pays off its contained arc', 'The revelation that widens the story', 'Emotional hook into Book Two — a cost, a choice, a new question'] },
      { roman: 'IV', name: 'Book 2 · Escalation', pct: 'Bk2 0–50%', purpose: 'The over-arc becomes the main plot; raise every stake established in Book One.',
        beats: ['Fallout from Book One’s ending', 'The over-arc antagonist in full', 'Midpoint of Book Two: point of no return'] },
      { roman: 'V', name: 'Book 2 · Climax & Full Resolution', pct: 'Bk2 50–100%', purpose: 'The final confrontation of the whole duology. Everything from both books closes.',
        beats: ['Darkest hour spanning both books’ stakes', 'The series climax — the over-arc resolved', 'Full resolution: no threads left open'] },
    ],
  },
  series: {
    intro: 'An ongoing arc across three or more books. Each book is a satisfying standalone adventure (its own problem, solved) while a series-long arc advances a step every volume and resolves in the finale. Plan Book One fully here; sketch the series spine so each book earns its place.',
    eyebrow: 'Series shape · each book complete, one arc across all',
    acts: [
      { roman: 'I', name: 'Book 1 · Setup (and series seeds)', pct: 'Bk1 Act 1', purpose: 'Introduce {hero}, the world, and this book’s problem — while planting the series-long question.',
        beats: ['Ordinary world; {hero}’s want; series-arc seed dropped quietly', 'Book One’s inciting incident', 'Commit to Book One’s contained quest'] },
      { roman: 'II', name: 'Book 1 · Rising & Midpoint', pct: 'Bk1 Act 2', purpose: 'Escalate Book One’s problem; the midpoint deepens both this book and the series arc.',
        beats: ['Try/fail on the Book One problem', 'Midpoint reframe — book AND series', 'Recurring antagonist/mystery for the series established'] },
      { roman: 'III', name: 'Book 1 · Climax & Standalone Win', pct: 'Bk1 Act 3', purpose: 'Resolve THIS book’s problem completely and satisfyingly. Advance the series arc one clear step. Leave a hook, not a cliff.',
        beats: ['Book One’s climax — its own problem solved', 'One step forward on the series-long arc', 'A hook that invites Book Two without cheating this ending'] },
      { roman: 'IV', name: 'The Series Spine (sketch)', pct: 'Across the series', purpose: 'Map how the over-arc advances each book, and what the finale resolves. Keep it light — it will change.',
        beats: ['Series-arc question and its final answer', 'One clear escalation per planned book', 'What changes permanently by the series’ end'] },
    ],
  },
};

// Section keys, for expand/collapse-all.
const SECTION_KEYS = ['foundation', 'world', 'cast', 'quickref', 'rel', 'factions', 'dynasty', 'spine', 'acts', 'timeline', 'threads', 'loose'];

// Plan completeness = BEATS ONLY (the Act Breakdown). No other section — and in
// particular not Houses & Factions or the Dynasty Map — contributes, so a
// house-less plan (housesEnabled off) still reaches 100%. Pure + exported so the
// invariant is unit-tested. Ignores beat TEXT; only counts + done flags matter.
export function computeBeatProgress(state) {
  const shape = STRUCTURES[state && state.shape] ? state.shape : 'standalone';
  const def = STRUCTURES[shape];
  const beatData = (state && state.beatData) || {};
  let total = 0, done = 0;
  def.acts.forEach((a, ai) => {
    const arr = beatData[`${shape}_${ai}`] || a.beats; // fallback = template beats (none done)
    total += arr.length;
    arr.forEach(b => { if (b && b.d) done += 1; });
  });
  return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
}

const DEFAULT_STATE = () => ({
  core: { title: '', hero: '', world: '' },
  fields: {},
  cast: [
    { id: uid(), badge: '1', protagonist: true, name: '', role: 'Protagonist', tier: 'primary', birthHouse: '', currentHouse: '', f: {} },
    { id: uid(), badge: '2', name: '', role: '', tier: 'secondary', birthHouse: '', currentHouse: '', f: {} },
  ],
  rel: [{ id: uid(), pair: '', f: {} }],
  factions: [{ id: uid(), name: '', type: '', fn: '', head: '', members: [{ n: '', r: '' }] }],
  dynasty: { links: [], allegiance: {}, houseColors: {} },
  beatData: {},
  shape: 'standalone',
  housesEnabled: true, // does this book have houses/dynasties? off → hide those sections
  ui: { examples: true },
  collapsed: {},
});

// Merge a loaded plan onto defaults so a partial/old shape never crashes.
function hydrate(loaded) {
  const d = DEFAULT_STATE();
  if (!loaded || typeof loaded !== 'object') return d;
  const dyn = loaded.dynasty && typeof loaded.dynasty === 'object' ? loaded.dynasty : {};
  return {
    core: { ...d.core, ...(loaded.core || {}) },
    fields: { ...(loaded.fields || {}) },
    cast: Array.isArray(loaded.cast) && loaded.cast.length ? loaded.cast : d.cast,
    rel: Array.isArray(loaded.rel) && loaded.rel.length ? loaded.rel : d.rel,
    factions: Array.isArray(loaded.factions) && loaded.factions.length ? loaded.factions : d.factions,
    dynasty: { links: Array.isArray(dyn.links) ? dyn.links : [], allegiance: dyn.allegiance || {}, houseColors: dyn.houseColors || {} },
    beatData: { ...(loaded.beatData || {}) },
    shape: STRUCTURES[loaded.shape] ? loaded.shape : 'standalone',
    housesEnabled: loaded.housesEnabled !== false, // absent → on (existing plans unchanged)
    ui: { examples: loaded.ui?.examples !== false },
    collapsed: { ...(loaded.collapsed || {}) },
  };
}

/* ═══════════════════════════════════════════════════════════
   SMALL PRESENTATION HELPERS
═══════════════════════════════════════════════════════════ */
function BookMark() {
  return (
    <span className="brand-mark">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
      </svg>
    </span>
  );
}
function Wire({ value, fallback }) {
  const val = (value || '').trim();
  const [flash, setFlash] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!val) return;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 420);
    return () => clearTimeout(t);
  }, [val]);
  return <span className={`wire${val ? '' : ' empty'}${flash ? ' flash' : ''}`}>{val || fallback}</span>;
}
function Example({ show, children }) {
  if (!show) return null;
  return <div className="example"><span className="ex-tag">Example</span>{children}</div>;
}
function Section({ num, glyph, overview, eyebrow, title, k, collapsed, onToggle, children }) {
  return (
    <section className={`section${overview ? ' overview' : ''}${collapsed ? ' collapsed' : ''}`}>
      <div className="section-head" onClick={() => onToggle(k)}>
        <span className={`section-num${glyph ? ' tl-num' : ''}`}>{num}</span>
        <div className="section-titles">
          <div className="section-eyebrow">{eyebrow}</div>
          <h2 className="section-title">{title}</h2>
        </div>
        <span className="chev">▾</span>
      </div>
      <div className="section-body">{children}</div>
    </section>
  );
}
// Allegiance shape at a House column head (oval/star/triangle/rect/default).

/* ═══════════════════════════════════════════════════════════
   THE TOOL
═══════════════════════════════════════════════════════════ */
function PlotPlannerTool({ licenceData, expired = false, signedIn = false, onSignOut, collab = null, onMyPlans = null }) {
  useAntiCopy();

  // Two persistence modes share ONE render tree. Solo (default): licence-gated,
  // wholesale save to plannerProgress (unchanged). Collab: state + writes come
  // from the injected collab api (plans/{planId}, live + per-path). The solo
  // hooks below no-op in collab so their behaviour is byte-identical when solo.
  const inCollab = !!collab;
  const collabApi = collab ? collab.api : null;

  const [soloState, setSoloState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [readOnly, setReadOnly] = useState(!!expired);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [showRenew, setShowRenew] = useState(false);
  const isTrial = licenceData?.term === 'trial';
  const canPersist = !inCollab && !!licenceData?.key && !String(licenceData.key).startsWith('SUB-');

  const loadedRef = useRef(false);
  const saveTimer = useRef(null);
  const stateRef = useRef(soloState);
  stateRef.current = soloState;

  /* ── Load (solo only) ── */
  useEffect(() => {
    if (inCollab) return undefined;
    let cancelled = false;
    (async () => {
      if (!canPersist) { if (!cancelled) { loadedRef.current = true; setLoading(false); } return; }
      try {
        const res = await getPlannerProgressFn({ key: licenceData.key, email: licenceData.email });
        if (cancelled) return;
        if (res.data?.expired) setReadOnly(true);
        if (res.data?.plan) setSoloState(hydrate(res.data.plan));
      } catch { /* leave defaults */ }
      finally { if (!cancelled) { loadedRef.current = true; setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [inCollab, canPersist, licenceData?.key, licenceData?.email]);

  /* ── Save (solo, debounced) ── */
  const save = useCallback(async () => {
    if (inCollab || readOnly || !canPersist) return;
    setSaveState('saving');
    try {
      await savePlannerProgressFn({ key: licenceData.key, email: licenceData.email, plan: stateRef.current });
      setSaveState('saved');
    } catch (err) {
      console.error('Planner save failed:', err?.message || err);
      setSaveState('error');
    }
  }, [inCollab, readOnly, canPersist, licenceData?.key, licenceData?.email]);

  const queueSave = useCallback(() => {
    if (inCollab || readOnly || !canPersist) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 500);
  }, [inCollab, readOnly, canPersist, save]);

  useEffect(() => { if (!inCollab && loadedRef.current) queueSave(); }, [inCollab, soloState, queueSave]);

  useEffect(() => {
    if (inCollab) return undefined;
    const warnOnLeave = (e) => { if (saveState === 'saving' || saveState === 'error') { e.preventDefault(); e.returnValue = ''; } };
    const onHide = () => { if (document.visibilityState === 'hidden' && saveState === 'error') save(); };
    window.addEventListener('beforeunload', warnOnLeave);
    document.addEventListener('visibilitychange', onHide);
    return () => { window.removeEventListener('beforeunload', warnOnLeave); document.removeEventListener('visibilitychange', onHide); };
  }, [inCollab, saveState, save]);

  // Seed beatData for the current shape's acts (solo only). In collab the beats
  // display via the beatsFor fallback and are materialised on first edit by
  // ensureBeats inside mutate — so nothing is written to the shared doc on load.
  useEffect(() => {
    if (inCollab || !loadedRef.current) return;
    setSoloState(prev => {
      const def = STRUCTURES[prev.shape] || STRUCTURES.standalone;
      const bd = { ...prev.beatData };
      let changed = false;
      def.acts.forEach((a, ai) => {
        const key = `${prev.shape}_${ai}`;
        if (!bd[key]) { bd[key] = a.beats.map(bt => ({ t: fill(bt, prev.core), d: false })); changed = true; }
      });
      return changed ? { ...prev, beatData: bd } : prev;
    });
  }, [inCollab, soloState.shape, loading]);

  // ── Collab load / access states (before we derive anything from state) ──
  if (inCollab && collabApi.notFound) {
    return <div className="plot-planner"><div className="expired-banner"><div className="expired-inner"><p><b>This plan couldn't be found.</b><br />It may have been deleted, or you no longer have access.</p><button className="btn solid" onClick={collab.onExit}>← Back to plans</button></div></div></div>;
  }
  if (inCollab && collabApi.error) {
    return <div className="plot-planner"><div className="expired-banner"><div className="expired-inner"><p><b>You don't have access to this plan.</b><br />Ask the owner to invite the email address you're signed in with.</p><button className="btn solid" onClick={collab.onExit}>← Back to plans</button></div></div></div>;
  }
  if (inCollab && !collabApi.state) {
    return <div className="plot-planner" style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(212,175,55,.3)', borderTopColor: '#d4af37', animation: 'ppspin 0.8s linear infinite' }} /><style>{'@keyframes ppspin{to{transform:rotate(360deg)}}'}</style></div>;
  }

  // ── Shared state surface (solo or collab) ──
  const state = inCollab ? collabApi.state : soloState;
  const setState = inCollab ? collabApi.setState : setSoloState;
  const dis = inCollab ? !!collabApi.readOnly : readOnly;
  const showLoading = inCollab ? false : loading;
  const { examples } = state.ui;
  const shapeDef = STRUCTURES[state.shape] || STRUCTURES.standalone;

  /* ── Mutators (structuredClone → imperative edit, like the prototype) ── */
  // In collab, mutate is the api's diff-and-write mutator; in solo it's the local
  // clone-and-setState (which the debounced save picks up). Same call sites.
  const mutate = inCollab
    ? collabApi.mutate
    : (fn) => { if (readOnly) return; setSoloState(prev => { const s = JSON.parse(JSON.stringify(prev)); fn(s); return s; }); };

  const setCore = (k, v) => mutate(s => { s.core[k] = v; });
  const setField = (id, v) => mutate(s => { s.fields[id] = v; });
  const f = (id) => state.fields[id] || '';
  const setShape = (shape) => mutate(s => { s.shape = shape; });
  const housesEnabled = state.housesEnabled !== false;
  const toggleHouses = () => mutate(s => { s.housesEnabled = (s.housesEnabled === false); });
  const toggleExamples = () => mutate(s => { s.ui.examples = !s.ui.examples; });
  const toggleSection = (key) => setState(prev => ({ ...prev, collapsed: { ...prev.collapsed, [key]: !prev.collapsed[key] } }));
  const expandAll = () => mutate(s => { s.collapsed = {}; s.cast.forEach(c => { c._collapsed = false; }); s.rel.forEach(r => { r._collapsed = false; }); s.factions.forEach(x => { x._collapsed = false; }); });
  const collapseAll = () => setState(prev => ({ ...prev, collapsed: SECTION_KEYS.reduce((a, k) => (a[k] = true, a), {}) }));

  // Cast
  const addCard = () => mutate(s => { s.cast.push({ id: uid(), badge: String(s.cast.length + 1), name: '', role: '', tier: 'secondary', birthHouse: '', currentHouse: '', f: {} }); });
  const delCard = (id) => mutate(s => {
    s.cast = s.cast.filter(c => c.id !== id);
    s.dynasty.links = (s.dynasty.links || []).filter(l => l.a !== id && l.b !== id);
  });
  const setCardName = (id, v) => mutate(s => {
    const c = s.cast.find(x => x.id === id); if (!c) return;
    if (c.protagonist) s.core.hero = v; else c.name = v;
  });
  const setCard = (id, k, v) => mutate(s => { const c = s.cast.find(x => x.id === id); if (c) c[k] = v; });
  const setCardField = (id, k, v) => mutate(s => { const c = s.cast.find(x => x.id === id); if (c) { c.f = c.f || {}; c.f[k] = v; } });
  const toggleCardCollapse = (id) => mutate(s => { const c = s.cast.find(x => x.id === id); if (c) c._collapsed = !c._collapsed; });

  // Relationships
  const addRel = () => mutate(s => { s.rel.push({ id: uid(), pair: '', f: {} }); });
  const delRel = (id) => mutate(s => { s.rel = s.rel.filter(r => r.id !== id); });
  const setRelPair = (id, v) => mutate(s => { const r = s.rel.find(x => x.id === id); if (r) r.pair = v; });
  const setRelField = (id, k, v) => mutate(s => { const r = s.rel.find(x => x.id === id); if (r) { r.f = r.f || {}; r.f[k] = v; } });
  const toggleRelCollapse = (id) => mutate(s => { const r = s.rel.find(x => x.id === id); if (r) r._collapsed = !r._collapsed; });

  // Factions
  const addFaction = () => mutate(s => { s.factions.push({ id: uid(), name: '', type: '', fn: '', head: '', members: [{ n: '', r: '' }] }); });
  const delFaction = (id) => mutate(s => { s.factions = s.factions.filter(x => x.id !== id); });
  const setFaction = (id, k, v) => mutate(s => { const fa = s.factions.find(x => x.id === id); if (fa) fa[k] = v; });
  const toggleFactionCollapse = (id) => mutate(s => { const fa = s.factions.find(x => x.id === id); if (fa) fa._collapsed = !fa._collapsed; });
  const addMember = (id) => mutate(s => { const fa = s.factions.find(x => x.id === id); if (fa) { fa.members = fa.members || []; fa.members.push({ n: '', r: '' }); } });
  const delMember = (id, mi) => mutate(s => { const fa = s.factions.find(x => x.id === id); if (fa) fa.members.splice(mi, 1); });
  const setMember = (id, mi, k, v) => mutate(s => { const fa = s.factions.find(x => x.id === id); if (fa && fa.members[mi]) fa.members[mi][k] = v; });

  // Dynasty
  const setAllegiance = (house, v) => mutate(s => { s.dynasty.allegiance = s.dynasty.allegiance || {}; s.dynasty.allegiance[house] = v; });
  const addDynLink = () => mutate(s => {
    const named = s.cast.filter(c => (c.protagonist ? (s.core.hero || c.name) : c.name || '').trim());
    if (named.length < 2) return;
    s.dynasty.links = s.dynasty.links || [];
    s.dynasty.links.push({ id: uid(), a: named[0].id, b: named[1].id, type: 'married' });
  });
  // Edit/remove ties BY ID (matches the collab model/store — plans store links as
  // an id-keyed map, so index-based edits would target the wrong link).
  const setLink = (id, k, v) => mutate(s => { const l = (s.dynasty.links || []).find(x => x.id === id); if (l) l[k] = v; });
  const delLink = (id) => mutate(s => { s.dynasty.links = (s.dynasty.links || []).filter(x => x.id !== id); });

  // Beats (beatData: { `${shape}_${ai}`: [{t,d}] })
  const beatsFor = (ai) => state.beatData[`${state.shape}_${ai}`] || shapeDef.acts[ai].beats.map(bt => ({ t: fill(bt, state.core), d: false }));
  const ensureBeats = (s, ai) => { const key = `${s.shape}_${ai}`; if (!s.beatData[key]) s.beatData[key] = (STRUCTURES[s.shape] || STRUCTURES.standalone).acts[ai].beats.map(bt => ({ t: fill(bt, s.core), d: false })); return s.beatData[key]; };
  const toggleBeat = (ai, bi) => mutate(s => { const arr = ensureBeats(s, ai); if (arr[bi]) arr[bi].d = !arr[bi].d; });
  const setBeatText = (ai, bi, v) => mutate(s => { const arr = ensureBeats(s, ai); if (arr[bi]) arr[bi].t = v; });
  const addBeat = (ai) => mutate(s => { ensureBeats(s, ai).push({ t: '', d: false }); });
  const delBeat = (ai, bi) => mutate(s => { ensureBeats(s, ai).splice(bi, 1); });

  const startFresh = () => {
    if (readOnly) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Start fresh? This clears everything in this plan and restores the blank planner with examples.')) return;
    loadedRef.current = true;
    setState(DEFAULT_STATE());
  };

  /* ── Derived: progress (beats across current shape — houses/dynasty never count) ── */
  const { done: beatDone, total: beatTotal, pct } = computeBeatProgress(state);

  /* ── Derived: dynasty columns ── */
  const dyn = buildDynasty(state);

  /* ── Renewal (solo only) ── */
  if (showRenew && !inCollab) {
    return <PricingPlans product={licenceData.scope || 'planner'} renewalKey={licenceData.key} defaultEmail={licenceData.email} fromTrial={isTrial} onBack={() => setShowRenew(false)} />;
  }

  const collapsed = (key) => !!state.collapsed[key];
  const countdown = (() => {
    if (inCollab) return null;
    const ms = licenceData?.expiresAtMs;
    if (readOnly || ms == null) return null;
    const left = ms - Date.now();
    if (left <= 0) return null;
    const days = Math.floor(left / 86400000);
    if (isTrial) return { text: days <= 0 ? 'Trial — expires today' : `Trial — ${days} day${days === 1 ? '' : 's'} left`, tone: days <= 3 ? 'rose' : '' };
    if (days > 30) return null;
    return { text: days <= 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`, tone: days <= 7 ? '' : 'muted', clickable: true };
  })();

  return (
    <div className="plot-planner">
      {showLoading ? (
        <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(212,175,55,.3)', borderTopColor: '#d4af37', animation: 'ppspin 0.8s linear infinite' }} />
        </div>
      ) : (
      <>
      {/* ── Masthead ── */}
      <div className="bar">
        <div className="brand-lockup">
          <BookMark />
          <div className="brand-text">
            <span className="n">The Plot Planner</span>
            <span className="s">Narratoria Ltd</span>
          </div>
        </div>
        <div className="bar-actions">
          {inCollab ? (
            <>
              <button className="btn ghost" onClick={collab.onExit} title="Back to your plans">← Plans</button>
              <span className="lic-pill"><Shield style={{ width: 12, height: 12 }} />{collab.isOwner ? 'Shared plan · owner' : 'Shared plan'}</span>
            </>
          ) : (
            <>
              {saveState === 'saving' && <span className="lic-pill muted">Saving…</span>}
              {saveState === 'saved' && <span className="lic-pill saved">Saved</span>}
              {saveState === 'error' && <button type="button" className="lic-pill rose" onClick={save}>Not saved — retry</button>}
              {countdown && (countdown.clickable
                ? <button type="button" className={`lic-pill ${countdown.tone}`} onClick={() => setShowRenew(true)}>{countdown.text}</button>
                : <span className={`lic-pill ${countdown.tone}`}>{countdown.text}</span>)}
              <span className={`lic-pill ${readOnly ? 'rose' : ''}`}><Shield style={{ width: 12, height: 12 }} />{readOnly ? 'Expired' : 'Licensed'}</span>
            </>
          )}
          <button className="btn ghost" onClick={expandAll}>Expand all</button>
          <button className="btn ghost" onClick={collapseAll}>Collapse all</button>
          <button className="btn ghost" onClick={toggleExamples}>{examples ? 'Hide examples' : 'Show examples'}</button>
          <button className="btn" onClick={() => window.print()}>Print / PDF</button>
          {!inCollab && onMyPlans && <button className="btn ghost" onClick={onMyPlans} title="Shared & collaborative plans">My plans</button>}
          {!inCollab && <button className="btn solid" onClick={startFresh} disabled={dis}>Start fresh</button>}
          <ToolNav current="planner" variant="ink" className="ml-1" />
          {signedIn && onSignOut && <button className="btn ghost" onClick={onSignOut} title="Sign out"><LogOut style={{ width: 12, height: 12 }} /></button>}
        </div>
      </div>

      {inCollab && (
        <CollaboratorControl
          meta={collab.meta}
          isOwner={collab.isOwner}
          onInvite={collab.onInvite}
          onRemove={collab.onRemove}
        />
      )}

      {!inCollab && readOnly && (
        <div className="expired-banner">
          <div className="expired-inner">
            <p><b>{isTrial ? 'Your free trial has ended — the planner is read-only.' : 'Your licence has expired — the planner is read-only.'}</b><br />
              Your plan is safe and still here to view. Nothing has been deleted. Renew to start editing again.</p>
            <button className="btn solid" onClick={() => setShowRenew(true)}>Renew</button>
          </div>
        </div>
      )}

      <header className="hero">
        <div className="kicker">The Pre-Writing Workbook</div>
        <h1>The <em>Plot</em> Planner</h1>
        <p className="lede">Plan any novel — standalone, duology, or series — before you write a word. Fill each detail once; it threads through the whole plan on its own.</p>
      </header>
      <hr className="hero-rule" />

      <div className="how">
        <div className="how-card">
          <div className="h-ic"><span className="dot" />Fill once, everywhere</div>
          <p>Type a name or place into a <b>gold-bordered field</b> and it flows to every mention below — the gold-underlined words. Change it once, it updates itself.</p>
        </div>
        <div className="how-card">
          <div className="h-ic"><span className="dot" />Worked examples</div>
          <p>Every prompt shows an example from a sample book, <em>The Salt Almanac</em>, so you're never staring at a blank box. Toggle them off when you're ready.</p>
        </div>
        <div className="how-card">
          <div className="h-ic"><span className="dot" />Your structure, your shape</div>
          <p>Pick <b>standalone, duology, or series</b> up top and the act plan reshapes to match — with guidance on where each book should break.</p>
        </div>
      </div>

      <div className="structure-panel">
        <div className="structure-inner">
          <div className="structure-label">Story shape
            <span className="sub">Choose how many books you're planning — the act breakdown adapts.</span>
          </div>
          <div className="seg" role="tablist" aria-label="Story shape">
            {['standalone', 'duology', 'series'].map(sh => (
              <button key={sh} className={state.shape === sh ? 'active' : ''} onClick={() => setShape(sh)} disabled={dis}>
                {sh[0].toUpperCase() + sh.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="structure-inner setup-toggle">
          <label className="switch-row">
            <input type="checkbox" checked={housesEnabled} onChange={toggleHouses} disabled={dis} />
            <span className="switch-track" aria-hidden="true"><span className="switch-thumb" /></span>
            <span className="switch-text">
              <span className="switch-title">Houses &amp; dynasties</span>
              <span className="sub">Switch this off for contemporary, literary or small-cast books with no noble houses or family trees — it hides the Houses &amp; Factions and Dynasty Map sections. Nothing is deleted; switch it back on any time.</span>
            </span>
          </label>
        </div>
      </div>

      <main>
        <div className="progress-shell">
          <div className="progress-label">Beats mapped <b>{beatDone}</b>/{beatTotal}</div>
          <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
          <div className="progress-label">{pct}%</div>
        </div>

        {/* 1 — Foundation */}
        <Section num="1" eyebrow="The one-line promise · fill the gold fields first" title="The Foundation" k="foundation" collapsed={collapsed('foundation')} onToggle={toggleSection}>
          <p className="intro">These are your anchor details. The gold-bordered fields are <b>core</b> — whatever you type flows into every mention throughout the plan. Start here.</p>
          <div className="two-col">
            <div className="field core"><label>Working title</label>
              <input type="text" value={state.core.title} onChange={e => setCore('title', e.target.value)} placeholder="Your title" disabled={dis} />
              <Example show={examples}><em>The Salt Almanac</em></Example></div>
            <div className="field core"><label>Protagonist's name</label>
              <input type="text" value={state.core.hero} onChange={e => setCore('hero', e.target.value)} placeholder="Main character" disabled={dis} />
              <Example show={examples}>Wren</Example></div>
          </div>
          <div className="field"><label>One-sentence premise</label>
            <div className="prompt">Who wants what, and what stands in the way? Name the feeling, not just the plot.</div>
            <textarea value={f('premise')} onChange={e => setField('premise', e.target.value)} placeholder="In one sentence…" disabled={dis} />
            <Example show={examples}>When the tide-charts that keep her drowned village alive start lying, <em>Wren</em> — the only person who can still read them — must sail to the source of the error before the next spring flood.</Example></div>
          <div className="two-col">
            <div className="field"><label>Genre &amp; audience</label>
              <textarea value={f('genre')} onChange={e => setField('genre', e.target.value)} placeholder="Genre, age group, comps" disabled={dis} />
              <Example show={examples}>Adult literary fantasy; readers of Erin Morgenstern &amp; Susanna Clarke.</Example></div>
            <div className="field"><label>The reader promise</label>
              <div className="prompt">What emotional experience are they buying? What should it never become?</div>
              <textarea value={f('promise')} onChange={e => setField('promise', e.target.value)} placeholder="The feeling you're promising…" disabled={dis} />
              <Example show={examples}>Slow wonder, salt-and-fog atmosphere, a quiet mystery. Never grimdark; melancholy but hopeful.</Example></div>
          </div>
          <div className="field"><label>Theme — what the book is really about (one sentence)</label>
            <textarea value={f('theme')} onChange={e => setField('theme', e.target.value)} placeholder="The argument under the plot…" disabled={dis} />
            <Example show={examples}>Inherited knowledge is worth nothing if you're too afraid to question it.</Example></div>
          <div className="field core"><label>Primary setting / world</label>
            <input type="text" value={state.core.world} onChange={e => setCore('world', e.target.value)} placeholder="Where it happens" disabled={dis} />
            <Example show={examples}>Thornmouth, a fog-bound coastal village</Example></div>
        </Section>

        {/* 2 — World & Rules */}
        <Section num="2" eyebrow="Only the rules that change scenes" title="World & Rules" k="world" collapsed={collapsed('world')} onToggle={toggleSection}>
          <p className="intro">Deep lore belongs in a separate archive. Here, capture only what controls what characters can and can't do in <Wire value={state.core.world} fallback="your setting" />.</p>
          <div className="two-col">
            <div className="field"><label>What ordinary people believe is true (public rule)</label>
              <textarea value={f('world_public')} onChange={e => setField('world_public', e.target.value)} placeholder="The accepted truth…" disabled={dis} />
              <Example show={examples}>The tide-charts are handed down perfect and unchanging; to doubt them is heresy.</Example></div>
            <div className="field"><label>What's actually true (hidden rule)</label>
              <textarea value={f('world_hidden')} onChange={e => setField('world_hidden', e.target.value)} placeholder="The secret truth…" disabled={dis} />
              <Example show={examples}>The charts were never finished. Each generation quietly guessed the missing pages.</Example></div>
          </div>
          <div className="two-col">
            <div className="field"><label>What magic / tech / power CANNOT do (limits)</label>
              <textarea value={f('world_limit')} onChange={e => setField('world_limit', e.target.value)} placeholder="The hard limits…" disabled={dis} />
              <Example show={examples}>Reading the tides shows what will happen, never how to stop it.</Example></div>
            <div className="field"><label>What using it costs</label>
              <textarea value={f('world_cost')} onChange={e => setField('world_cost', e.target.value)} placeholder="The price…" disabled={dis} />
              <Example show={examples}>Each reading takes a memory the reader can't choose — usually a happy one.</Example></div>
          </div>
          <div className="field"><label>Key factions, places or institutions</label>
            <div className="prompt">Name it, its job in the story, and who controls it.</div>
            <textarea value={f('world_factions')} onChange={e => setField('world_factions', e.target.value)} placeholder="One per line…" disabled={dis} />
            <Example show={examples}>The Almanac Hall — keepers of the charts, run by the Warden. The Drowned Quarter — the poorest, floods first, no vote in Hall decisions.</Example></div>
        </Section>

        {/* 3 — Characters */}
        <Section num="3" eyebrow="Goal · want · wound · arc" title="Characters" k="cast" collapsed={collapsed('cast')} onToggle={toggleSection}>
          <p className="intro">One card each. The first card is pre-linked to your protagonist, <Wire value={state.core.hero} fallback="your protagonist" /> — filled up top. Add a card for every character who can change the story.</p>
          <div className="cards-wrap">
            {TIERS.map(([tk, tlabel]) => {
              const grp = state.cast.filter(c => (c.tier || 'secondary') === tk);
              if (!grp.length) return null;
              return (
                <React.Fragment key={tk}>
                  <div className="tier-head">{tlabel}</div>
                  {grp.map(c => (
                    <CharacterCard key={c.id} c={c} examples={examples} dis={dis} heroName={state.core.hero} showHouses={housesEnabled}
                      onName={setCardName} onCard={setCard} onField={setCardField} onDelete={delCard} onToggle={toggleCardCollapse} />
                  ))}
                </React.Fragment>
              );
            })}
          </div>
          <button className="add-btn" onClick={addCard} disabled={dis}>+ Add a character</button>
        </Section>

        {/* Cast at a Glance */}
        <Section glyph num="◆" overview eyebrow="Generated from your character cards · keep it open while you draft" title="Cast at a Glance" k="quickref" collapsed={collapsed('quickref')} onToggle={toggleSection}>
          <p className="intro">The physical quick-reference — eyes, hair, build — for every character in one place, so a description never contradicts itself two hundred pages later. Fill the Appearance box on each card above and it lands here.</p>
          <div className="qr-wrap"><QuickRef cast={state.cast} heroName={state.core.hero} /></div>
        </Section>

        {/* 4 — Relationships */}
        <Section num="4" eyebrow="Track the bond, not the backstory" title="Key Relationships" k="rel" collapsed={collapsed('rel')} onToggle={toggleSection}>
          <p className="intro">For each important bond, note where it stands now and the next shift the story has earned — this stops relationships lurching too fast.</p>
          <div className="cards-wrap">
            {state.rel.map(r => (
              <RelationshipCard key={r.id} r={r} examples={examples} dis={dis} onPair={setRelPair} onField={setRelField} onDelete={delRel} onToggle={toggleRelCollapse} />
            ))}
          </div>
          <button className="add-btn" onClick={addRel} disabled={dis}>+ Add a relationship</button>
        </Section>

        {housesEnabled && (<>
        {/* Houses & Factions */}
        <Section glyph num="⌂" overview eyebrow="Organisations & who sits where · the power map" title="Houses & Factions" k="factions" collapsed={collapsed('factions')} onToggle={toggleSection}>
          <p className="intro">The institutions that run your world — Houses, committees, guilds — and the people inside each, ranked from the top down. Give a faction a head and add its members to watch the hierarchy take shape.</p>
          <div className="cards-wrap">
            {state.factions.map(fa => (
              <FactionCard key={fa.id} fa={fa} dis={dis} onSet={setFaction} onDelete={delFaction} onToggle={toggleFactionCollapse}
                onAddMember={addMember} onDelMember={delMember} onSetMember={setMember} />
            ))}
          </div>
          <button className="add-btn" onClick={addFaction} disabled={dis}>+ Add a House / faction</button>
        </Section>

        {/* Dynasty Map */}
        <Section glyph num="✦" overview eyebrow="Who's married, allied, feuding · the whole web at a glance" title="Dynasty Map" k="dynasty" collapsed={collapsed('dynasty')} onToggle={toggleSection}>
          <p className="intro">Your world's Houses laid out as columns. Each column is a House someone <b>lives in or married into</b>; every person is a box <b>coloured by the House they were born into</b> — so a box that doesn't match its column married in from elsewhere. A House's <b>allegiance</b> (from your Houses &amp; Factions) shows under its name. Marriages are joined with ⋈.</p>
          <div className="dyn-mapwrap"><DynastyColumns dyn={dyn} /></div>
          <DynastyLegend dyn={dyn} />
          <p className="dyn-note">Columns and colours come from your <b>characters</b> — set each one's <b>Born into</b> and <b>Current house</b> on its card above. Assign each House an allegiance (from your Houses &amp; Factions) on the left; record marriages on the right.</p>
          <div className="dyn-editor2">
            <div className="dyn-col2">
              <div className="hier-label">Houses &amp; allegiances</div>
              <div>
                {dyn.houseList.length === 0
                  ? <p className="qr-empty">Houses appear here once your characters have them.</p>
                  : dyn.houseList.map(h => {
                    const namedFactions = dyn.factions.filter(fa => (fa.name || '').trim());
                    return (
                      <div className="dyn-hrow" key={h}>
                        <span className="dyn-hname">{h}</span>
                        <select className="dyn-select" value={state.dynasty.allegiance?.[h] || ''} onChange={e => setAllegiance(h, e.target.value)} disabled={dis || namedFactions.length === 0}>
                          <option value="">— allegiance —</option>
                          {namedFactions.map(fa => <option key={fa.id} value={fa.id}>{fa.name}</option>)}
                        </select>
                      </div>
                    );
                  })}
                {dyn.houseList.length > 0 && dyn.factions.filter(fa => (fa.name || '').trim()).length === 0 && (
                  <p className="dyn-note">Add your groups in Houses &amp; Factions above, then choose each House's allegiance here.</p>
                )}
              </div>
            </div>
            <div className="dyn-col2">
              <div className="hier-label">Marriages &amp; ties</div>
              <div>
                {dyn.named.length < 2
                  ? <p className="qr-empty">Name at least two characters above to link them.</p>
                  : state.dynasty.links.map((l) => (
                    <div className="dyn-lrow" key={l.id}>
                      <select className="dyn-select" value={l.a} onChange={e => setLink(l.id, 'a', e.target.value)} disabled={dis}>
                        {dyn.named.map(c => <option key={c.id} value={c.id}>{dyn.name(c)}</option>)}
                      </select>
                      <select className="dyn-select dyn-type" value={l.type} onChange={e => setLink(l.id, 'type', e.target.value)} disabled={dis}>
                        {Object.keys(LINK_TYPES).map(k => <option key={k} value={k}>{LINK_TYPES[k].label}</option>)}
                      </select>
                      <select className="dyn-select" value={l.b} onChange={e => setLink(l.id, 'b', e.target.value)} disabled={dis}>
                        {dyn.named.map(c => <option key={c.id} value={c.id}>{dyn.name(c)}</option>)}
                      </select>
                      <button className="hier-del" onClick={() => delLink(l.id)} title="Remove tie" disabled={dis}>✕</button>
                    </div>
                  ))}
              </div>
              <button className="add-btn" onClick={addDynLink} disabled={dis || dyn.named.length < 2}>+ Add a tie</button>
            </div>
          </div>
        </Section>
        </>)}

        {/* 5 — Plot Spine */}
        <Section num="5" eyebrow="Five sentences that hold the book up" title="The Plot Spine" k="spine" collapsed={collapsed('spine')} onToggle={toggleSection}>
          <p className="intro">If these five hold, every scene has a job. When a scene sags, check which one it's serving.</p>
          <div className="field"><label>External plot — what <Wire value={state.core.hero} fallback="the protagonist" /> DOES</label>
            <textarea value={f('spine_external')} onChange={e => setField('spine_external', e.target.value)} placeholder="The outward journey…" disabled={dis} />
            <Example show={examples}>Wren sails upcoast to find who's been altering the charts.</Example></div>
          <div className="field"><label>Internal arc — how they CHANGE</label>
            <textarea value={f('spine_internal')} onChange={e => setField('spine_internal', e.target.value)} placeholder="The inner journey…" disabled={dis} />
            <Example show={examples}>From guardian of inherited truth to someone brave enough to rewrite it.</Example></div>
          <div className="field"><label>Relationship arc — the heart-line</label>
            <textarea value={f('spine_rel')} onChange={e => setField('spine_rel', e.target.value)} placeholder="The bond that carries the feeling…" disabled={dis} />
            <Example show={examples}>A prickly alliance with the smuggler who ferries her becomes trust, then more.</Example></div>
          <div className="field"><label>Thematic argument — what the book SAYS</label>
            <textarea value={f('spine_theme')} onChange={e => setField('spine_theme', e.target.value)} placeholder="The point…" disabled={dis} />
            <Example show={examples}>Tradition without questioning is just a slow drowning.</Example></div>
          <div className="field"><label>Dramatic question — the yes/no the reader is asking all book</label>
            <textarea value={f('spine_question')} onChange={e => setField('spine_question', e.target.value)} placeholder="Will she…?" disabled={dis} />
            <Example show={examples}>Will Wren fix the charts before the spring flood — and at what cost to what she believed?</Example></div>
        </Section>

        {/* 6 — Act Breakdown */}
        <Section num="6" eyebrow={`${shapeDef.eyebrow.split(' · ')[0]} · tick beats as you draft`} title="The Act Breakdown" k="acts" collapsed={collapsed('acts')} onToggle={toggleSection}>
          <p className="intro">{fill(shapeDef.intro, state.core)}</p>
          <div>
            {shapeDef.acts.map((a, ai) => {
              const arr = beatsFor(ai);
              const showNote = state.shape !== 'standalone' && (a.name.includes('Climax') || a.name.includes('Crack') || a.name.includes('Standalone Win'));
              return (
                <div className="act" key={ai}>
                  <div className="act-top">
                    <div className="act-roman">{a.roman}</div>
                    <div className="act-name">{a.name}<span className="a-pct">{a.pct}</span></div>
                  </div>
                  <div className="act-body">
                    <div className="act-purpose">{fill(a.purpose, state.core)}</div>
                    {showNote && <div className="struct-note"><b>Series craft:</b> end this book on a complete, satisfying beat that also opens the next — an emotional hook or a new question, never an abrupt cut mid-scene.</div>}
                    <ul className="beat-list">
                      {arr.map((b, bi) => (
                        <li key={bi} className={b.d ? 'done' : ''}>
                          <input type="checkbox" className="beat-check" checked={!!b.d} onChange={() => toggleBeat(ai, bi)} disabled={dis} aria-label="Mark beat done" />
                          <input className="beat-text" value={b.t} placeholder="Describe this beat…" onChange={e => setBeatText(ai, bi, e.target.value)} disabled={dis} />
                          {!dis && <button className="beat-del" onClick={() => delBeat(ai, bi)} title="Delete this beat">✕</button>}
                        </li>
                      ))}
                    </ul>
                    {!dis && <button className="add-btn" onClick={() => addBeat(ai)}>+ Add a beat</button>}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Story Timeline */}
        <Section glyph num="◆" overview eyebrow="Generated from your acts & beats · updates as you tick" title="Story Timeline" k="timeline" collapsed={collapsed('timeline')} onToggle={toggleSection}>
          <p className="intro">The shape of your book, drawn straight from the Act Breakdown above — each act sits in order, sized to its share of the story, and every beat carries across. Tick a beat and the bars fill.</p>
          <div className="tl-overall-row"><span className="tl-overall-label">Progress</span>
            <span className="tl-overall">{beatDone} of {beatTotal} beats mapped · {pct}%</span></div>
          <div className="tl-track">
            {shapeDef.acts.map((a, ai) => {
              const arr = beatsFor(ai);
              const doneN = arr.filter(b => b.d).length;
              const m = (a.pct || '').match(/(\d+)\s*[–-]\s*(\d+)/);
              const grow = m ? Math.max(1, (+m[2]) - (+m[1])) : 1;
              return (
                <div className="tl-lane" key={ai} style={{ flexGrow: grow }}>
                  <div className="tl-head">
                    <span className="tl-roman">{a.roman}</span>
                    <div className="tl-htext"><div className="tl-name">{a.name}</div><div className="tl-pct">{a.pct}</div></div>
                    <div className="tl-meta">{doneN}/{arr.length}</div>
                  </div>
                  <div className="tl-fill"><div className="tl-fill-in" style={{ width: `${arr.length ? Math.round(doneN / arr.length * 100) : 0}%` }} /></div>
                  <div className="tl-beats">
                    {arr.map((b, bi) => (
                      <div className={`tl-beat${b.d ? ' done' : ''}`} key={bi}>
                        <span className="tl-mark" /><span className="tl-btext">{b.t || '(new beat)'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* 7 — Threads */}
        <Section num="7" eyebrow="Continuity you can't afford to drop" title="Threads, Secrets & Promises" k="threads" collapsed={collapsed('threads')} onToggle={toggleSection}>
          <p className="intro">Readers forgive mystery; they notice neglect. Track what's open, who knows what, and every promise the opening pages make.</p>
          <div className="field"><label>Open plot threads</label><div className="prompt">The thread · where it's introduced · its planned payoff.</div>
            <textarea value={f('threads_open')} onChange={e => setField('threads_open', e.target.value)} placeholder="One per line…" disabled={dis} />
            <Example show={examples}>The missing final chart page — hinted ch.2 — turns out to be deliberately hidden by the Warden; payoff at the climax.</Example></div>
          <div className="field"><label>Secrets — and who is WRONG about them</label><div className="prompt">The secret · who knows · who suspects · who's wrong · when the reader learns.</div>
            <textarea value={f('threads_secrets')} onChange={e => setField('threads_secrets', e.target.value)} placeholder="One per line…" disabled={dis} />
            <Example show={examples}>The charts were always guesswork. The Warden knows; the village believes them holy; <em>Wren</em> is wrong until the midpoint.</Example></div>
          <div className="field"><label>Promises the opening makes to the reader</label><div className="prompt">Emotional, mystery, and payoff promises you must honour.</div>
            <textarea value={f('threads_promises')} onChange={e => setField('threads_promises', e.target.value)} placeholder="What you've promised…" disabled={dis} />
            <Example show={examples}>A quiet mystery with a real answer; a slow-burn romance that pays off; the flood threat resolved, not hand-waved.</Example></div>
        </Section>

        {/* 8 — Open Decisions & Naming */}
        <Section num="8" eyebrow="Park decisions here so they don't stall you" title="Open Decisions & Naming" k="loose" collapsed={collapsed('loose')} onToggle={toggleSection}>
          <p className="intro">Everything still undecided in one place, so a blank you haven't filled never stops you drafting.</p>
          <div className="field"><label>Names still to lock</label>
            <textarea value={f('loose_names')} onChange={e => setField('loose_names', e.target.value)} placeholder="Characters, places, terms awaiting a name…" disabled={dis} />
            <Example show={examples}>The smuggler (currently 'the smuggler'); the upcoast city; the name of the flood-season festival.</Example></div>
          <div className="field"><label>Questions to answer before you draft</label>
            <textarea value={f('loose_questions')} onChange={e => setField('loose_questions', e.target.value)} placeholder="The decisions you're circling…" disabled={dis} />
            <Example show={examples}>Does the Warden get a redemption? First person or third? How magical is the chart-reading, exactly?</Example></div>
          <div className="field"><label>Ideas parked for later (or a sequel)</label>
            <textarea value={f('loose_parked')} onChange={e => setField('loose_parked', e.target.value)} placeholder="Good ideas that don't fit yet…" disabled={dis} />
            <Example show={examples}>The lighthouse-keeper subplot — too much for book one; hold it.</Example></div>
        </Section>
      </main>

      <footer>
        <hr className="foot-rule" />
        <div className="foot-brand"><BookMark /><span className="fb-text">Narratoria Ltd</span></div>
        {inCollab
          ? <p className="foot-licence">{collab.isOwner ? 'Your plan' : 'Shared with you'}{collab.meta?.ownerEmail ? <> · owner <b>{collab.meta.ownerEmail}</b></> : null}</p>
          : <p className="foot-licence">Licenced to: <b>{licenceData.email}</b></p>}
        <p>Your plan saves to your account automatically — use <b>Print / PDF</b> to keep a copy or share it with a co-writer or editor.</p>
        <p>This tool is licenced for personal use only. Redistribution, resale, or sharing of access is prohibited.</p>
        <p className="pwg">Part of the Publishing Without a Gatekeeper toolkit · <a className="foot-back" href="/publishing-roadmap">The Publishing Roadmap →</a></p>
      </footer>
      <style>{'@keyframes ppspin{to{transform:rotate(360deg)}}'}</style>
      </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CARD + OVERVIEW SUB-COMPONENTS
═══════════════════════════════════════════════════════════ */
function CharacterCard({ c, examples, dis, heroName, showHouses = true, onName, onCard, onField, onDelete, onToggle }) {
  const nameVal = c.protagonist ? (heroName || '') : (c.name || '');
  return (
    <div className={`card${c._collapsed ? ' collapsed' : ''}`}>
      <div className="card-top" onClick={(e) => { if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.closest('.card-actions')) return; onToggle(c.id); }}>
        <div className="card-badge">{c.badge || '•'}</div>
        <div className="card-title">
          <input className={`ct-name${c.protagonist ? ' protagonist' : ''}`} value={nameVal} placeholder="Character name" disabled={dis} onChange={e => onName(c.id, e.target.value)} />
          <input className="ct-role" value={c.role || ''} placeholder="role" disabled={dis} onChange={e => onCard(c.id, 'role', e.target.value)} />
        </div>
        <div className="card-actions">
          {c.protagonist ? <span className="mini lock">Linked to §1</span> : (!dis && <button className="mini del" onClick={() => onDelete(c.id)}>Delete</button>)}
        </div>
      </div>
      <div className="card-body">
        <div className="char-meta">
          {showHouses && (<>
          <div className="cm-cell"><label>Born into (House)</label>
            <input type="text" className="cm-input" placeholder="birth house — colour" value={c.birthHouse || ''} disabled={dis} onChange={e => onCard(c.id, 'birthHouse', e.target.value)} /></div>
          <div className="cm-cell"><label>Current house</label>
            <input type="text" className="cm-input" placeholder="where they live — column" value={c.currentHouse || c.affiliation || ''} disabled={dis} onChange={e => onCard(c.id, 'currentHouse', e.target.value)} /></div>
          </>)}
          <div className="cm-cell"><label>Importance</label>
            <select className="cm-select" value={c.tier || 'secondary'} disabled={dis} onChange={e => onCard(c.id, 'tier', e.target.value)}>
              {TIER_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
        </div>
        <div className="appearance">
          <div className="app-label">Appearance · quick reference</div>
          <div className="app-grid">
            {APPEARANCE_FIELDS.map(([key, label, ph]) => (
              <div className="app-cell" key={key}>
                <label>{label}</label>
                <input type="text" className="app-input" placeholder={ph} value={c.f?.[key] || ''} disabled={dis} onChange={e => onField(c.id, key, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
        {CAST_FIELDS.map(([key, label, prompt, ex]) => (
          <div className="field" key={key}>
            <label>{label}</label>
            {prompt ? <div className="prompt">{prompt}</div> : null}
            <textarea value={c.f?.[key] || ''} onChange={e => onField(c.id, key, e.target.value)} disabled={dis} />
            {examples && ex ? <Example show={examples}>{ex}</Example> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function RelationshipCard({ r, examples, dis, onPair, onField, onDelete, onToggle }) {
  return (
    <div className={`card${r._collapsed ? ' collapsed' : ''}`}>
      <div className="card-top" onClick={(e) => { if (e.target.tagName === 'INPUT' || e.target.closest('.card-actions')) return; onToggle(r.id); }}>
        <div className="card-badge" style={{ fontSize: '1.1rem' }}>↔</div>
        <div className="card-title"><input className="ct-name" value={r.pair || ''} placeholder="e.g. Wren ↔ the smuggler" disabled={dis} onChange={e => onPair(r.id, e.target.value)} /></div>
        <div className="card-actions">{!dis && <button className="mini del" onClick={() => onDelete(r.id)}>Delete</button>}</div>
      </div>
      <div className="card-body">
        {REL_FIELDS.map(([key, label, prompt, ex]) => (
          <div className="field" key={key}>
            <label>{label}</label>
            {prompt ? <div className="prompt">{prompt}</div> : null}
            <textarea value={r.f?.[key] || ''} onChange={e => onField(r.id, key, e.target.value)} disabled={dis} />
            {examples && ex ? <Example show={examples}>{ex}</Example> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function FactionCard({ fa, dis, onSet, onDelete, onToggle, onAddMember, onDelMember, onSetMember }) {
  return (
    <div className={`card${fa._collapsed ? ' collapsed' : ''}`}>
      <div className="card-top" onClick={(e) => { if (e.target.tagName === 'INPUT' || e.target.closest('.card-actions')) return; onToggle(fa.id); }}>
        <div className="card-badge" style={{ fontSize: '1.15rem' }}>⌂</div>
        <div className="card-title">
          <input className="ct-name" value={fa.name || ''} placeholder="House / faction name" disabled={dis} onChange={e => onSet(fa.id, 'name', e.target.value)} />
          <input className="ct-role" value={fa.type || ''} placeholder="type — House, Committee, Guild…" disabled={dis} onChange={e => onSet(fa.id, 'type', e.target.value)} />
        </div>
        <div className="card-actions">{!dis && <button className="mini del" onClick={() => onDelete(fa.id)}>Delete</button>}</div>
      </div>
      <div className="card-body">
        <div className="field"><label>What it does in the story</label>
          <textarea value={fa.fn || ''} onChange={e => onSet(fa.id, 'fn', e.target.value)} disabled={dis} /></div>
        <div className="hier">
          <div className="hier-label">Hierarchy</div>
          <div className="hier-head">
            <input type="text" className="hier-head-input" placeholder="Head / who controls it" value={fa.head || ''} disabled={dis} onChange={e => onSet(fa.id, 'head', e.target.value)} />
          </div>
          <div className="hier-members">
            {(fa.members || []).map((m, mi) => (
              <div className="hier-member" key={mi}>
                <input type="text" className="hier-name" placeholder="member" value={m.n || ''} disabled={dis} onChange={e => onSetMember(fa.id, mi, 'n', e.target.value)} />
                <input type="text" className="hier-rank" placeholder="rank / role" value={m.r || ''} disabled={dis} onChange={e => onSetMember(fa.id, mi, 'r', e.target.value)} />
                {!dis && <button className="hier-del" title="Remove member" onClick={() => onDelMember(fa.id, mi)}>✕</button>}
              </div>
            ))}
          </div>
          {!dis && <button className="add-btn hier-add" onClick={() => onAddMember(fa.id)}>+ Add member</button>}
        </div>
      </div>
    </div>
  );
}

function QuickRef({ cast, heroName }) {
  if (!cast || !cast.length) return <p className="qr-empty">Add characters above and their looks appear here.</p>;
  const nameOf = (c) => c.protagonist ? (heroName || c.name || '') : (c.name || '');
  return (
    <table className="qr-table">
      <thead><tr>{['Character', 'Eyes', 'Hair', 'Build', 'Height', 'Distinguishing features'].map(h => <th key={h}>{h}</th>)}</tr></thead>
      <tbody>
        {cast.map(c => (
          <tr key={c.id}>
            <td className="qr-name">
              <div className="qr-nm">{nameOf(c) || 'Unnamed'}</div>
              {c.role ? <div className="qr-role">{c.role}</div> : null}
            </td>
            {['eyes', 'hair', 'build', 'height', 'features'].map(k => <td key={k}>{(c.f && c.f[k]) || '—'}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Compute the dynasty column model from cast + dynasty (ported from drawMap).
function buildDynasty(state) {
  const core = state.core;
  const name = (c) => (c.protagonist ? (core.hero || c.name || '') : (c.name || ''));
  const named = state.cast.filter(c => name(c).trim());
  const links = (state.dynasty && state.dynasty.links) || [];
  const res = (c) => ((c.currentHouse || c.affiliation || '').trim() || 'Unaffiliated');
  const allHouses = [];
  named.forEach(c => [res(c), (c.birthHouse || '').trim()].forEach(h => { if (h && allHouses.indexOf(h) < 0) allHouses.push(h); }));
  const dynasty = state.dynasty || {};
  // Stable per-house colour (name hash → HSL), author-override-aware. Not palette-
  // indexed, so adding/removing a house never reshuffles the others' colours.
  const hcol = (h) => resolveHouseColour(dynasty, h);
  // Allegiance options + resolution come from the author's OWN factions (id → name).
  const factions = state.factions || [];
  const allegianceName = (h) => resolveAllegianceName(factions, dynasty.allegiance || {}, h);
  const cols = [];
  named.forEach(c => { const h = res(c); if (cols.indexOf(h) < 0) cols.push(h); });
  const byId = (id) => named.find(c => c.id === id) || null;
  const spouseOf = (c) => {
    for (const l of links) { if (String(l.type).indexOf('married') === 0) { if (l.a === c.id) return byId(l.b); if (l.b === c.id) return byId(l.a); } }
    return null;
  };
  // House list for the allegiance editor = unique current+birth across named.
  const houseList = [];
  named.forEach(c => [(c.currentHouse || c.affiliation || '').trim(), (c.birthHouse || '').trim()].forEach(h => { if (h && houseList.indexOf(h) < 0) houseList.push(h); }));
  return { core, name, named, links, res, allHouses, hcol, cols, byId, spouseOf, houseList,
    factions, allegianceName, alleg: (state.dynasty && state.dynasty.allegiance) || {} };
}

function PersonBox({ c, dyn }) {
  const birth = (c.birthHouse || '').trim();
  const col = birth ? dyn.hcol(birth) : '#efe4c8';
  const tier = c.tier || 'secondary';
  return <span className={`dyn-person tier-${tier}`} style={{ background: col, color: txtOn(col) }} title={dyn.name(c) + (birth ? ` · born into ${birth}` : '')}>{dyn.name(c) || '—'}</span>;
}

function DynastyColumns({ dyn }) {
  if (!dyn.named.length) return <p className="qr-empty">Add named characters with a current house, and the columns build themselves.</p>;
  return (
    <div className="dyn-cols">
      {dyn.cols.map(house => {
        const people = dyn.named.filter(c => dyn.res(c) === house);
        const done = {};
        const rows = [];
        people.forEach(c => {
          if (done[c.id]) return;
          const sp = dyn.spouseOf(c);
          if (sp && dyn.res(sp) === house && !done[sp.id]) {
            rows.push(<div className="dyn-couple" key={c.id}><PersonBox c={c} dyn={dyn} /><span className="dyn-x">⋈</span><PersonBox c={sp} dyn={dyn} /></div>);
            done[c.id] = 1; done[sp.id] = 1;
          } else if (sp) {
            rows.push(<div className="dyn-prow" key={c.id}><PersonBox c={c} dyn={dyn} /><span className="dyn-note-x">⋈ {dyn.name(sp)} <em>({dyn.res(sp)})</em></span></div>);
            done[c.id] = 1;
          } else {
            rows.push(<div className="dyn-prow" key={c.id}><PersonBox c={c} dyn={dyn} /></div>);
            done[c.id] = 1;
          }
        });
        return (
          <div className="dyn-col-house" key={house}>
            <div className="dyn-col-head" style={{ borderBottomColor: dyn.hcol(house) }}>
              <span className="dyn-house-swatch" style={{ background: dyn.hcol(house) }} aria-hidden="true" />
              <div className="dyn-house-name">{house}</div>
              {dyn.allegianceName(house) && <div className="dyn-house-alleg">{dyn.allegianceName(house)}</div>}
            </div>
            <div className="dyn-col-body">{rows}</div>
          </div>
        );
      })}
    </div>
  );
}

function DynastyLegend({ dyn }) {
  if (!dyn.named.length) return <div className="dyn-legend" />;
  return (
    <div className="dyn-legend">
      <div className="dyn-leg-group">
        <span className="dyn-leg-cap">Born into:</span>
        {dyn.allHouses.map(h => (
          <span className="dyn-leg-item" key={h}><span className="dyn-sw" style={{ background: dyn.hcol(h) }} /><span>{h}</span></span>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ROOT — gate wiring (mirrors PublishingRoadmap, product='planner')
═══════════════════════════════════════════════════════════ */
// Collaborative editor: subscribes to plans/{planId} and drives the shared
// workbench in collab mode. A separate component so useCollabPlan is only ever
// mounted on a collab route.
function CollabEditor({ planId, authUser, onExit, onSignOut }) {
  const api = useCollabPlan({ planId, authUser });
  const onInvite = (email) => invitePlanCollaboratorFn({ planId, email }).then((r) => r.data);
  const onRemove = (email) => removeCollaboratorFn({ planId, email }).then((r) => r.data);
  return (
    <PlotPlannerTool
      signedIn={!!authUser}
      onSignOut={onSignOut}
      collab={{ api, meta: api.meta, isOwner: api.isOwner, onInvite, onRemove, onExit }}
    />
  );
}

export default function PlotPlanner({ authorSubscription } = {}) {
  const {
    checking, licenceData, expired, authUser, isFreeAuthor, isPaidOrAdmin,
    setUnlocked, handleSignOut: signOutLicence,
  } = useLicence('planner', { authorSubscription, anyTool: true });   // Story Bible: included with every plan

  // Collaboration routing (in addition to the solo licence flow): `?plan=<id>`
  // opens the collaborative editor, `?view=myplans` the plan picker. Anything
  // else is the unchanged solo tool. (`?view=plans`/`pricing` still = pricing.)
  const readRoute = () => {
    if (typeof window === 'undefined') return { kind: 'solo' };
    const params = new URLSearchParams(window.location.search);
    if (params.get('plan')) return { kind: 'plan', planId: params.get('plan') };
    if (params.get('view') === 'myplans') return { kind: 'picker' };
    return { kind: 'solo' };
  };
  const [route, setRoute] = useState(readRoute);
  const pushRoute = (next) => {
    if (typeof window !== 'undefined' && window.history?.pushState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('plan'); url.searchParams.delete('view');
      if (next.kind === 'plan') url.searchParams.set('plan', next.planId);
      else if (next.kind === 'picker') url.searchParams.set('view', 'myplans');
      window.history.pushState(null, '', url.pathname + url.search + url.hash);
    }
    setRoute(next);
  };
  const goPlan = (id) => pushRoute({ kind: 'plan', planId: id });
  const goPicker = () => pushRoute({ kind: 'picker' });
  const goSolo = () => pushRoute({ kind: 'solo' });

  const wantsPlansParam = typeof window !== 'undefined'
    ? ['plans', 'pricing'].includes(new URLSearchParams(window.location.search).get('view'))
    : false;
  const [showBuy, setShowBuy] = useState(wantsPlansParam);
  const [googleEmail, setGoogleEmail] = useState('');

  const syncViewParam = (show) => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (show) url.searchParams.set('view', 'plans'); else url.searchParams.delete('view');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  };
  const openPlans = (email = '') => { setGoogleEmail(email); setShowBuy(true); syncViewParam(true); };
  const closePlans = () => { setShowBuy(false); setGoogleEmail(''); syncViewParam(false); };
  const handleSignOut = async () => { await signOutLicence(); setShowBuy(false); setGoogleEmail(''); };

  // Entitlement to create your OWN plan: a planner/bundle licence, or a paid
  // author. Editing plans shared with you is free regardless.
  const entitled = !!licenceData || !!isPaidOrAdmin;
  const doGoogleSignIn = () => { signInWithPopup(auth, googleProvider).catch(() => {}); };
  const handleNewPlan = async () => {
    // Server-authoritative: the callable decides who may create (superadmin /
    // entitled). On refusal, show pricing only once the planner is live — while
    // dark it stays invite/admin-only, so a refusal is a quiet no-op.
    try {
      const res = await createPlanFn({});
      if (res.data?.planId) goPlan(res.data.planId);
    } catch { if (!PLANNER_DARK) openPlans(); }
  };
  const collabFeedback = (label) => (
    <RoadmapFeedbackWidget surface="planner" pagePath="/plot-planner" pageContext={label} email={(authUser && authUser.email) || ''} />
  );
  // The collab picker (Google sign-in when signed out) — the dark-launch entry.
  // Shown both on the ?view=myplans route and in place of the licence gate on the
  // bare /plot-planner route, so a curious visitor never sees a gate or pricing.
  const pickerView = (
    <PlanPicker authUser={authUser} entitled={entitled} onOpen={goPlan} onNewPlan={handleNewPlan} onSignIn={doGoogleSignIn} />
  );

  // ── Collab routes short-circuit the licence flow ──
  if (route.kind === 'plan') {
    if (!authUser) {
      return (
        <>
          <div className="plot-planner picker"><div className="picker-head">
            <h1>Open shared plan</h1>
            <p className="picker-lede">Sign in with the email your invitation was sent to, and this plan opens.</p>
            <button className="btn solid" onClick={doGoogleSignIn}>Sign in with Google</button>{' '}
            <button className="btn ghost" onClick={goSolo}>Cancel</button>
          </div></div>
          {collabFeedback('planner (collab sign-in)')}
        </>
      );
    }
    return (
      <>
        <CollabEditor planId={route.planId} authUser={authUser} onExit={goPicker} onSignOut={async () => { await signOutLicence(); goSolo(); }} />
        {collabFeedback('planner (collab plan)')}
      </>
    );
  }
  if (route.kind === 'picker') {
    return (
      <>
        {pickerView}
        {collabFeedback('planner (picker)')}
      </>
    );
  }

  let content, viewLabel;
  if (checking) {
    content = (
      <div className="plot-planner" style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(212,175,55,.3)', borderTopColor: '#d4af37', animation: 'ppspin 0.8s linear infinite' }} />
        <style>{'@keyframes ppspin{to{transform:rotate(360deg)}}'}</style>
      </div>
    );
    viewLabel = 'planner (loading)';
  } else if (isFreeAuthor && !licenceData) {
    content = <PricingPlans product="planner" onUnlock={(data, exp) => setUnlocked(data, exp)} onBack={null} />;
    viewLabel = 'planner (free author → plans)';
  } else if (showBuy) {
    content = (
      <PricingPlans product="planner" defaultEmail={googleEmail} signedIn={!!googleEmail}
        onUnlock={(data, exp) => { setUnlocked(data, exp); closePlans(); }} onBack={closePlans} />
    );
    viewLabel = 'planner (pricing plans)';
  } else if (licenceData) {
    content = <PlotPlannerTool licenceData={licenceData} expired={expired} signedIn={!!authUser} onSignOut={handleSignOut} onMyPlans={goPicker} />;
    viewLabel = 'planner (tool)';
  } else {
    // No licence → show the collab picker (Google sign-in), NOT a licence gate or
    // planner pricing. This is the dark-launch entry for a curious visitor.
    content = pickerView;
    viewLabel = 'planner (picker)';
  }

  let feedbackEmail = licenceData?.email || googleEmail || '';
  if (!feedbackEmail && typeof window !== 'undefined') {
    try { feedbackEmail = JSON.parse(localStorage.getItem('ncc_planner_licence') || 'null')?.email || ''; } catch { /* ignore */ }
  }

  return (
    <>
      {content}
      <RoadmapFeedbackWidget surface="planner" pagePath="/plot-planner" pageContext={viewLabel} email={feedbackEmail} />
    </>
  );
}
